import { useEffect, useState } from 'react';
import { findTourTarget } from './tour-steps.js';

/**
 * The two facts about the page the address bar does not carry: whether a modal dialog is open,
 * and whether the customer page is showing its measurements pane. Both decide the step (see
 * `resolveTourStep`), and neither is anybody's React state the tour can read, so it watches the
 * DOM — batched to one look per frame, and only while the tour is on.
 */
export interface TourDom {
  /** The open modal `<dialog>`, if any. The app's dialogs are native and open in the top layer. */
  dialog: HTMLDialogElement | null;
  measurementsTabOpen: boolean;
}

const NOTHING: TourDom = { dialog: null, measurementsTabOpen: false };

function look(): TourDom {
  return {
    dialog: document.querySelector<HTMLDialogElement>('dialog[open]'),
    measurementsTabOpen: findTourTarget('measure-add') !== null,
  };
}

export function useTourDom(enabled: boolean): TourDom {
  const [dom, setDom] = useState<TourDom>(NOTHING);

  useEffect(() => {
    if (!enabled) {
      setDom(NOTHING);
      return;
    }
    let frame = 0;
    const update = () => {
      frame = 0;
      const next = look();
      setDom((prev) =>
        prev.dialog === next.dialog && prev.measurementsTabOpen === next.measurementsTabOpen
          ? prev
          : next,
      );
    };
    update();
    const observer = new MutationObserver(() => {
      if (!frame) frame = window.requestAnimationFrame(update);
    });
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['open', 'data-tour', 'aria-selected', 'style', 'class'],
    });
    return () => {
      observer.disconnect();
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, [enabled]);

  return dom;
}
